import { lazy, Suspense, useState, type FC } from 'react';
import * as translator from '../../../utils/translator';
import { MainTracker } from '../../../PixelTrackers/MainTracker';
import sepaInstantIcon from '../../../icons/sepa-instant.svg';
import type { CheckoutPlan, CheckoutSection } from '../checkoutPlan';
import type { PaymentCallbacks, PaymentContext, SecondaryPaymentTypeDTO, UserClickTracking } from '../paymentTypes';
import type { CheckoutStyleVariant } from './checkoutFormProps';
import { SectionForm, useCheckoutSections } from './useCheckoutSections';
import './CheckoutStack.css';

export interface CheckoutStackProps {
  plan: CheckoutPlan;
  paymentContext: PaymentContext;
  callbacks: PaymentCallbacks;
  tracking: UserClickTracking;
  variant?: CheckoutStyleVariant;
  // The host's Stripe form hides its billing country (Care+ v3 page)
  hideBillingCountry?: boolean;
}

// Tile icon per payment_types.code; these codes also have a translated fallback title
// (payment/secondaryPaymentTypes/<code>). The DB title wins.
const TILE_META: Record<string, { icon: string }> = {
  paybybank: { icon: sepaInstantIcon },
  pay_by_bank_stripe: { icon: sepaInstantIcon },
};

/**
 * The payment page's checkout: the order's PSP widget, plus one tile per secondary payment type
 * (task-dev-plans/multi-psp-checkout-sections.md §6.3). With a single section it renders the host
 * form exactly as the page did before, with the page's callbacks and context.
 */
const SectionCheckoutStack: FC<CheckoutStackProps> = ({ plan, paymentContext, callbacks, tracking, variant, hideBillingCountry }) => {
  const { sections, handleLoadError, sectionContexts, submittingSectionRef, callbacksFor, triggerSubmitOf } = useCheckoutSections(plan, paymentContext, callbacks);
  const hostSection = sections.find((section) => section.role === 'host');
  const secondarySections = sections.filter((section) => section.role === 'secondary');

  const [requestedActiveId, setRequestedActiveId] = useState<string>(plan.sections[0]?.id ?? '');
  const activeSectionId = sections.some((section) => section.id === requestedActiveId) ? requestedActiveId : (sections[0]?.id ?? '');

  const [selectedNative, setSelectedNative] = useState<Record<string, string>>({});

  const activate = (sectionId: string) => {
    if (submittingSectionRef.current && submittingSectionRef.current !== sectionId) return;
    setRequestedActiveId(sectionId);
  };

  const selectedNativeOf = (section: CheckoutSection) => selectedNative[section.id] ?? section.nativeMethods[0];

  // A single section is the host form exactly as the page rendered it before.
  const isSingle = plan.sections.length === 1;

  const renderForm = (section: CheckoutSection) => {
    const onError = (error: unknown) => handleLoadError(section, error);
    const sectionProps = isSingle ? { paymentContext, callbacks, hideBillingCountry } : {
      paymentContext: sectionContexts[section.id],
      callbacks: callbacksFor(section),
      ...(section.role === 'secondary' ? { allowedMethods: [selectedNativeOf(section)], hideExpressCheckout: true } : { hideBillingCountry }),
      onLoadError: onError,
      onActivate: () => activate(section.id),
    };
    return <SectionForm section={section} tracking={tracking} variant={variant} onError={onError} {...sectionProps} />;
  };

  if (isSingle) {
    return sections[0] ? renderForm(sections[0]) : null;
  }

  // The page's ID verification modal submits through the active section.
  callbacks.onTriggerPaymentSubmit = () => { triggerSubmitOf(activeSectionId); };

  const sectionClassName = (section: CheckoutSection) =>
    `checkout-section checkout-section--${section.role}${section.id === activeSectionId ? '' : ' checkout-section--inactive'}`;

  const tileTitle = (type: SecondaryPaymentTypeDTO) =>
    type.title || (TILE_META[type.code] ? translator.translate(paymentContext.language, 'payment', `secondaryPaymentTypes/${type.code}`) : null) || type.code;

  const selectTile = (section: CheckoutSection, type: SecondaryPaymentTypeDTO) => {
    if (submittingSectionRef.current && submittingSectionRef.current !== section.id) return;
    if (selectedNativeOf(section) !== type.nativeType) {
      setSelectedNative((current) => ({ ...current, [section.id]: type.nativeType }));
    }
    activate(section.id);
    MainTracker.track('custom', 'Payment Method Selected', { selected_payment_method: type.nativeType, provider: type.provider });
  };

  return (
    <div className="checkout-stack">
      {hostSection && (
        <div className={sectionClassName(hostSection)}>
          {renderForm(hostSection)}
          {hostSection.id !== activeSectionId && (
            <button
              type="button"
              className="checkout-section__activate"
              aria-label={translator.translate(paymentContext.language, 'payment', 'checkoutSections/useThisMethod')}
              onClick={() => activate(hostSection.id)}
            />
          )}
        </div>
      )}

      {secondarySections.map((section) => {
        const selected = selectedNativeOf(section);
        return (
          <div key={section.id} className={sectionClassName(section)} data-provider={section.provider}>
            {section.paymentTypes.map((type) => {
              const isSelected = section.id === activeSectionId && type.nativeType === selected;
              return (
                <button
                  key={type.paymentTypeId}
                  type="button"
                  className={`checkout-tile${isSelected ? ' checkout-tile--selected' : ''}`}
                  aria-pressed={isSelected}
                  onClick={() => selectTile(section, type)}
                >
                  <span className={`checkout-tile__radio${isSelected ? ' checkout-tile__radio--selected' : ''}`} aria-hidden="true" />
                  <span className="checkout-tile__title">{tileTitle(type)}</span>
                  {TILE_META[type.code] && <img className="checkout-tile__icon" src={TILE_META[type.code].icon} alt="" />}
                </button>
              );
            })}
            <div className="checkout-section__body">
              {renderForm(section)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// The flat method list (primer-payment-page-redesign.md §4.1) loads only on pages that use it.
const FlatCheckoutStack = lazy(() => import('./FlatCheckoutStack'));

const CheckoutStack: FC<CheckoutStackProps> = (props) => (props.plan.layout === 'flat'
  ? <Suspense fallback={null}><FlatCheckoutStack {...props} /></Suspense>
  : <SectionCheckoutStack {...props} />);

export default CheckoutStack;
